import * as React from "react";
import { Avatar as AvatarPrimitive } from "radix-ui";

import { cn } from "@/lib/utils";

type AvatarSize = "2xs" | "xs" | "sm" | "md" | "default" | "lg";

/** The square's corner tracks its size, so a 16px avatar doesn't read round. */
const SQUARE_RADIUS: Record<AvatarSize, string> = {
  "2xs": "rounded-xs",
  xs: "rounded-xs",
  sm: "rounded-md",
  md: "rounded-md",
  default: "rounded-lg",
  lg: "rounded-lg"
};

function Avatar({
  className,
  size = "default",
  shape = "square",
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Root> & {
  /** 16, 20, 24, 28, 32 (default) and 40px. */
  size?: AvatarSize;
  shape?: "circle" | "square";
}) {
  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      data-size={size}
      data-shape={shape}
      className={cn(
        "group/avatar relative flex size-8 shrink-0 select-none after:absolute after:inset-0 after:rounded-[inherit] after:border after:border-line-2 data-[size=2xs]:size-4 data-[size=xs]:size-5 data-[size=sm]:size-6 data-[size=md]:size-7 data-[size=lg]:size-10",
        // Not a data-[shape] variant: its higher specificity would beat a
        // caller's `rounded-*`, and tailwind-merge could not dedupe it.
        shape === "square" ? SQUARE_RADIUS[size] : "rounded-full",
        className
      )}
      {...props}
    />
  );
}

function AvatarImage({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Image>) {
  return (
    <AvatarPrimitive.Image
      data-slot="avatar-image"
      className={cn(
        "aspect-square size-full rounded-[inherit] object-cover",
        className
      )}
      {...props}
    />
  );
}

function AvatarFallback({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Fallback>) {
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      className={cn(
        "flex size-full items-center justify-center rounded-[inherit] bg-fill-2 text-sm font-medium text-fg-2 group-data-[size=2xs]/avatar:text-2xs group-data-[size=xs]/avatar:text-2xs group-data-[size=sm]/avatar:text-xs group-data-[size=md]/avatar:text-xs",
        className
      )}
      {...props}
    />
  );
}

function AvatarBadge({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="avatar-badge"
      className={cn(
        "absolute right-0 bottom-0 z-10 inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground bg-blend-color ring-cutout ring-background select-none",
        "group-data-[size=sm]/avatar:size-2 group-data-[size=sm]/avatar:[&>svg]:hidden",
        "group-data-[size=default]/avatar:size-2.5 group-data-[size=default]/avatar:[&>svg]:size-2",
        "group-data-[size=lg]/avatar:size-3 group-data-[size=lg]/avatar:[&>svg]:size-2",
        className
      )}
      {...props}
    />
  );
}

function AvatarGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="avatar-group"
      className={cn(
        "group/avatar-group flex -space-x-2 *:data-[slot=avatar]:ring-2 *:data-[slot=avatar]:ring-background",
        className
      )}
      {...props}
    />
  );
}

function AvatarGroupCount({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="avatar-group-count"
      className={cn(
        "relative flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted group-has-data-[size=sm]/avatar-group:rounded-md text-sm text-muted-foreground ring-cutout ring-background group-has-data-[size=lg]/avatar-group:size-10 group-has-data-[size=sm]/avatar-group:size-6 [&>svg]:size-4 group-has-data-[size=lg]/avatar-group:[&>svg]:size-5 group-has-data-[size=sm]/avatar-group:[&>svg]:size-3",
        className
      )}
      {...props}
    />
  );
}

export {
  Avatar,
  AvatarImage,
  AvatarFallback,
  AvatarGroup,
  AvatarGroupCount,
  AvatarBadge
};
